import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImpactReportSlider } from '@/impactReports/ImpactReportSlider.atom';
import type { FormElement } from '@/formbuilder/formBuilder.constants';
import { ReportContentWorksheetAndUrl } from '@/reportEditor/components/reportContentModal/shared/ReportContentWorksheetAndUrl.molecule';
import { SelectWorksheet } from '@/worksheets/SelectWorksheet.molecule';
import { Button, Icon } from '@seeqdev/qomponents';
import _ from 'lodash';

import type { ImpactReportCategory, ImpactType, Location, Stages } from '@/impactReports/impactReport.types';
import {
  availableStages,
  IMPACT_OWNER_FIRSTNAME,
  IMPACT_OWNER_LASTNAME,
  IMPACT_REPORT_LABEL_ID,
  instructions,
  stageIcons,
  TRACK_IMPACT,
  DAILY_FREQUENCY,
  WEEKLY_FREQUENCY,
  MONTHLY_FREQUENCY,
  QUARTERLY_FREQUENCY,
  YEARLY_FREQUENCY,
} from '@/impactReports/impactReport.types';
import { sqContextApi } from '@/sdk/api/ContextApi';
import { sqWorkbooksApi } from '@/sdk/api/WorkbooksApi';
import { sqImpactReportStore, sqWorkbenchStore, sqWorkbookStore } from '@/core/core.stores';
import { SaveAndContinueFormBuilder } from '@/formbuilder/SaveAndContinueFormBuilder.page';
import type { IdentityPreviewV1 } from '@/sdk/model/IdentityPreviewV1';
import { useAsyncEffect } from 'rooks';
import { randomUUID, validateGuid } from '@/utilities/utilities';
import {
  addOrUpdateImpactReport,
  getImpactReportTableId,
  giveEveryoneGroupReadPermissions,
  removeEveryoneGroupReadPermissions,
  stageDisplayStringByIndex,
  verifyEveryoneGroupReadPermissions,
} from '@/impactReports/impactReport.utilities';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import type { ScalarPropertyV1 } from '@/sdk/model/ScalarPropertyV1';
import type { SelectedSearchItem } from '@/search/search.types';
import { SelectedWorksheetDisplayAndLink } from '@/impactReports/SelectedWorksheetDisplayAndLink.atom';
import { doTrack } from '@/track/track.service';
import type { ContextLabelWithIdInputV1 } from '@/sdk/model/ContextLabelWithIdInputV1';
import { ImpactReportAIOverlay } from '@/impactReports/ValueCaptureAIOverlay.atom';
import { genAIEnabled } from '@/services/systemConfiguration.utilities';
import { useFluxPaths } from '@/core/hooks/useFluxPaths.hook';
import { setUpdateNominations } from '@/impactReports/impactReport.actions';
import {
  getCustomCategoryTitle,
  getKPIDisplayName,
  getKPIItemColumnName,
  getKPIUnit,
} from '@/impactReports/impactReportDisplay.utilities';
import { errorToast } from '@/utilities/toast.utilities';
import moment from 'moment-timezone';
import { logError } from '@/utilities/logger';

interface ImpactReportModalProps {
  existingReport?: string;
  stageIndex?: number;
  workbookId?: string;
  worksheetId?: string;
  setWorkbookId: (id: string) => void;
  setWorksheetId: (id: string) => void;
  closeFn: (reloadTable?: boolean) => void;
  setStageIndex: (idx: number) => void;
  report?: Record<string, any>;
  categories?: ImpactReportCategory[];
}

interface ImpactReportHeaderProps {
  workbookId: string;
  worksheetId: string;
  stageIndex: number;
  setStageIndex: (idx: number) => void;
  setWorkbookId: (id: string) => void;
  setWorksheetId: (id: string) => void;
}

const validSuffixes = ['/h', '/d', '/min', '/mo', '/ms', '/ns', '/s', '/wk', '/y', '']; // '' for just the unit itself, e.g. '$'

function isValidUnit(unit: string): boolean {
  return validSuffixes.some((suffix) => unit.endsWith(suffix));
}

const getValidUnitForDisplay = (unit: string) => (unit.indexOf('·') ? unit.substring(0, unit.indexOf('·')) : unit);

export const ImpactReportHeader: React.FunctionComponent<ImpactReportHeaderProps> = ({
  workbookId,
  worksheetId,
  stageIndex = 0,
  setStageIndex,
  setWorksheetId,
  setWorkbookId,
}) => {
  const { t } = useTranslation();
  const [currentStage, setCurrentStage] = useState<Stages | null>(null);
  const [currentStageDisplay, setCurrentStageDisplay] = useState('');

  useEffect(() => {
    setCurrentStage(availableStages[stageIndex]);
    setCurrentStageDisplay(t(`IMPACT_REPORT.${availableStages[stageIndex]?.toUpperCase()}`));
  }, [stageIndex]);

  return (
    <div className="flexRowContainer valueCapture width-maximum">
      <div className="mb5">
        <h4>{t('IMPACT_REPORT.CAPTURE_IMPACT')}</h4>
      </div>
      <div className="flexColumnContainer flexWrap">
        {workbookId && worksheetId ? (
          <div className="flexRowContainer mt0 flexAlignStart flexFill">
            <div>{t('IMPACT_REPORT.SELECTED_WORKSHEET')}</div>
            <div>
              <SelectedWorksheetDisplayAndLink workbookId={workbookId} worksheetId={worksheetId} />
              <Icon
                icon="fa-close"
                onClick={() => {
                  setWorkbookId('');
                  setWorksheetId('');
                  setStageIndex(0);
                }}
                extraClassNames="ml10 mt3 cursorPointer"
              />
            </div>
          </div>
        ) : (
          <div className="flexFill" />
        )}
        {currentStage && (
          <div className="flexRowContainer flexAlignEnd">
            <h6 className="mtn8">
              {t('IMPACT_REPORT.CURRENT_STAGE')} {currentStageDisplay}
            </h6>
            <ImpactReportSlider setStageIdx={setStageIndex} stageIndex={stageIndex} />
          </div>
        )}
      </div>
    </div>
  );
};

export const ImpactReportForm: React.FunctionComponent<ImpactReportModalProps> = ({
  stageIndex = 0,
  workbookId,
  worksheetId,
  setWorkbookId,
  setWorksheetId,
  setStageIndex,
  closeFn,
  report,
  categories,
}) => {
  const isTopic = useFluxPath(sqWorkbookStore, () => sqWorkbookStore.isTopic);
  const kpiColumns = useFluxPath(sqImpactReportStore, () => sqImpactReportStore.kpiColumns);
  const { showSite, showLocation, showAssetPath } = useFluxPaths(sqImpactReportStore, [
    'showSite',
    'showLocation',
    'showAssetPath',
  ]);
  const { t } = useTranslation();
  const [impactType, setImpactType] = useState<ImpactType>('oneTime');
  const [rowDatumId, setRowDatumId] = useState<string | undefined>(
    report && report[SeeqNames.MaterializedTables.DatumIdColumn],
  );
  // we don't need a store, but we need to store the values in local state to ensure we can persist them across stages
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState(genAIEnabled() ? '' : instructions);
  const [owner, setOwner] = useState({
    id: sqWorkbenchStore.currentUser.id,
    name: sqWorkbenchStore.currentUser.name,
  });
  const [customCategories, setCustomCategories] = useState<{ [key: string]: any }>({});
  const [showItemSelection, setShowItemSelection] = useState<{ [columnName: string]: boolean }>({});

  const [asset, setAsset] = useState('');
  const [impactStartDate, setImpactStartDate] = useState<moment.Moment>(moment().subtract(1, 'year'));
  const [impactEndDate, setImpactEndDate] = useState<moment.Moment | undefined>(undefined);
  const [impactFrequency, setImpactFrequency] = useState('');
  const [location, setLocation] = useState<Location | undefined>(undefined);
  const [customKPIValues, setCustomKPIValues] = useState<{ [key: string]: any }>({});
  const [site, setSite] = useState(sqWorkbenchStore.currentUser.site);
  const [forceUpdateText, setForceUpdateText] = useState(description);
  // this is a temporary solution - for now we do not support generating AI description for Vantage
  const [isVantage, setIsVantage] = useState(false);
  const [hasReadPermissions, setHasReadPermissions] = useState(false);

  const readPermissionRef = React.useRef(false);

  const impactFrequencyOptions = [
    { value: DAILY_FREQUENCY, label: t('IMPACT_REPORT.FREQUENCIES.DAILY') },
    { value: WEEKLY_FREQUENCY, label: t('IMPACT_REPORT.FREQUENCIES.WEEKLY') },
    { value: MONTHLY_FREQUENCY, label: t('IMPACT_REPORT.FREQUENCIES.MONTHLY') },
    { value: QUARTERLY_FREQUENCY, label: t('IMPACT_REPORT.FREQUENCIES.QUARTERLY') },
    { value: YEARLY_FREQUENCY, label: t('IMPACT_REPORT.FREQUENCIES.YEARLY') },
  ];

  useAsyncEffect(async () => {
    if (!workbookId) return;
    const everyoneCanRead = await verifyEveryoneGroupReadPermissions(workbookId);
    setHasReadPermissions(everyoneCanRead);
    readPermissionRef.current = everyoneCanRead;
  }, [workbookId]);

  useAsyncEffect(async () => {
    if (!workbookId) return;
    const workbookResponse = await sqWorkbooksApi.getWorkbook({ id: workbookId });
    const vantage = workbookResponse.data?.type === SeeqNames.Types.Vantage;
    setIsVantage(vantage);
  }, [workbookId]);
  // end temporary code

  const [showAIOverlay, setShowAIOverlay] = useState(description === '');
  const tableId = useFluxPath(sqImpactReportStore, () => sqImpactReportStore.tableId);

  useAsyncEffect(async () => {
    await getImpactReportTableId();
  }, []);

  useEffect(() => {
    setShowAIOverlay(!isVantage && description === '');
    if (isVantage && !description) {
      setForceUpdateText(instructions);
    }
  }, [isVantage, description]);

  useEffect(() => {
    if (!report || _.isEmpty(report)) return;
    setAsset(report[SeeqNames.ImpactReport.ImpactAsset]);
    setTitle(report[SeeqNames.ImpactReport.ImpactName]);
    setDescription(report[SeeqNames.ImpactReport.ImpactDescription]);
    setImpactEndDate(
      report[SeeqNames.ImpactReport.ImpactEndDate] && moment(report[SeeqNames.ImpactReport.ImpactEndDate]),
    );
    setImpactStartDate(
      report[SeeqNames.ImpactReport.ImpactStartDate] && moment(report[SeeqNames.ImpactReport.ImpactStartDate]),
    );
    setImpactFrequency(report[SeeqNames.ImpactReport.ImpactFrequency]);
    setWorksheetId(report[SeeqNames.ImpactReport.AssignedToWorksheet]);
    setWorkbookId(report[SeeqNames.ImpactReport.AssignedToWorkbook]);
    setImpactType(report[SeeqNames.ImpactReport.ImpactSavingsType]);
    setShowItemSelection(
      kpiColumns?.reduce((acc, column) => {
        const value = _.has(customKPIValues, column.columnName)
          ? customKPIValues[column.columnName]
          : report[`${column.columnName}_item`];
        acc[column.columnName] = validateGuid(value);
        return acc;
      }, {} as Record<string, boolean>),
    );
    setSite(report[SeeqNames.ImpactReport.ImpactSite]);
    if (report[SeeqNames.ImpactReport.Location]) {
      setLocation(JSON.parse(report[SeeqNames.ImpactReport.Location]));
    }
    setOwner({
      id: report[SeeqNames.ImpactReport.ImpactOwner],
      name: `${report[IMPACT_OWNER_FIRSTNAME]} ${report[IMPACT_OWNER_LASTNAME]}`,
    } as IdentityPreviewV1);

    const customCats: Record<string, string>[] | undefined =
      categories?.map((category) => {
        const cat = category.category;
        return { [cat.id!]: report[cat.id!] };
      }) || [];
    setCustomCategories(customCats);

    // custom KPIs can be using calculated items, so we need to ensure we set the state values to the saved item and
    // not the calculated impact as that will lose the item reference
    const kpiValues: Record<string, string | number> = kpiColumns?.reduce((acc, column) => {
      const itemColumName = getKPIItemColumnName(column.columnName);
      if (!report[itemColumName]) {
        report[itemColumName] = report[column.columnName];
      }
      const possibleItemId = report[itemColumName];
      acc[column.columnName] = validateGuid(possibleItemId) ? { id: possibleItemId } : possibleItemId;
      return acc;
    }, {} as Record<string, number | string>);
    setCustomKPIValues(kpiValues);
  }, [report]);

  const sectionHeader = (stageIndex: number) => (
    <>
      <div className={`flexColumnContainer flexAlignCenter mbn16 formSectionHeader ${stageIndex !== 0 ? 'mt20' : ''}`}>
        <Icon icon={stageIcons[stageIndex]} extraClassNames="mr10 ml5" />
        <h4 className="flexFill">{stageDisplayStringByIndex(stageIndex)}</h4>
      </div>
      <hr />
    </>
  );

  if (!tableId) return null;

  const kpiFields = kpiColumns
    ? kpiColumns.map((kpi) => {
        const value = _.has(customKPIValues, kpi.columnName)
          ? customKPIValues[kpi.columnName]
          : report && report[kpi.columnName];
        return {
          component: 'ToggleInputSelectItemSearchFormComponent' as const,
          label: `${getKPIDisplayName(kpi.columnName)} (${getKPIUnit(kpi.columnName)})`,
          name: kpi.columnName,
          onChange: (item: string | SelectedSearchItem[] | number | null) => {
            if (typeof item === 'string' || typeof item === 'number') {
              setCustomKPIValues({ ...customKPIValues, [kpi.columnName]: item });
            } else if (typeof item === 'object') {
              if (item?.length === 1) {
                const selectedItem = item[0] as any;
                // verify the units - if they are invalid we have to make sure we let the user know or they can end
                // up with a very broken table :(
                const itemUnit = selectedItem['valueUnitOfMeasure'];
                const kpiUnit = getKPIUnit(kpi.columnName);
                if (
                  (kpiUnit.indexOf('·') > -1 && itemUnit === kpiUnit.replace('·', '/')) ||
                  itemUnit === kpiUnit.substring(0, kpiUnit.indexOf('·')) ||
                  (itemUnit?.startsWith(kpiUnit) && isValidUnit(itemUnit))
                ) {
                  setCustomKPIValues({ ...customKPIValues, [kpi.columnName]: selectedItem });
                  return;
                }
                errorToast({
                  messageKey: 'IMPACT_REPORT.INVALID_UNIT',
                  messageParams: { unit: getKPIUnit(kpi.columnName) },
                });
              }
            }
          },
          unit: _.find(impactFrequencyOptions, { value: impactFrequency })?.label,
          required: false,
          showItemSelection: showItemSelection[kpi.columnName],
          setShowItemSelection: (columnName: string, shouldShowSelection: boolean) => {
            setShowItemSelection((prev) => ({ ...prev, [columnName]: shouldShowSelection }));
          },
          size: 'sm' as const,
          testId: kpi.columnName,
          value: validateGuid(value?.id) ? [value] : value,
          isMultiple: false,
          notSelectedMessage: t('IMPACT_REPORT.FIND_CUSTOM_CALC'),
          title: t('IMPACT_REPORT.FIND_CUSTOM_CALC'),
          overrideWorkbookId: workbookId,
          scopeIds: [workbookId],
          searchTypes: ['Signal'],
          showToggle: impactType !== 'oneTime',
          iconPartialTooltipKey: 'IMPACT_REPORT.CUSTOM_CALC_TOOLTIP',
          customCalcInstructions: t('IMPACT_REPORT.CUSTOM_CALC_INSTRUCTIONS', {
            unit: getValidUnitForDisplay(getKPIUnit(kpi.columnName)),
          }),
        } as FormElement;
      })
    : [];

  const categoryFields = categories
    ? categories.map((category) => {
        const cat = category.category;
        const fieldName = getCustomCategoryTitle(category);
        const options = category.options;
        const categoryId = cat.id!;
        return {
          label: fieldName,
          component: 'SelectFormComponent',
          name: categoryId,
          value: customCategories[categoryId] ? customCategories[categoryId] : report && report[categoryId],
          required: false,
          options: options as any,
          onChange: (value) => {
            setCustomCategories({ ...customCategories, [categoryId]: value });
          },
        } as FormElement;
      })
    : [];

  const updateCKContent = (content: string) => {
    setDescription(description + content);
    setForceUpdateText(description + content);
  };

  const formDefinition: FormElement[] = [
    {
      component: 'FormGroup',
      name: 'identify',
      includeIf: stageIndex >= 0,
      components: [
        {
          component: 'DisplayOnlyFormElementWrapper',
          name: 'identifyHeader',
          children: sectionHeader(0),
        },
        {
          component: 'TextFieldFormComponent',
          name: 'title',
          label: 'Title',
          value: title,
          required: true,
          onChange: (title) => {
            setTitle(title as string);
          },
          size: 'sm',
          testId: 'formTitle',
        },
        {
          component: 'DisplayOnlyFormElementWrapper',
          name: 'CKEditorLabel',
          children: [
            <div className="flexColumnContainer flexAlignCenter mb5" key="CKLabel">
              {t('IMPACT_REPORT.DESCRIPTION')}
              {genAIEnabled() && !showAIOverlay && worksheetId && workbookId && !isVantage && (
                <Icon
                  onClick={() => setShowAIOverlay(true)}
                  icon="fa-sparkles"
                  iconPrefix="fa-solid"
                  extraClassNames="ml5 cursorPointer"
                />
              )}
            </div>,
          ],
        },
        {
          component: 'CKEditorFormComponent',
          name: 'description',
          label: '',
          extraClassNames: 'width-maximum',
          value: description,
          required: true,
          onChange: (description) => {
            setDescription(description as string);
          },
          testId: 'description',
          forceUpdateText,
          overlay:
            genAIEnabled() && showAIOverlay ? (
              <ImpactReportAIOverlay
                onClose={() => setShowAIOverlay(false)}
                updateCKContent={updateCKContent}
                worksheetId={worksheetId}
                workbookId={workbookId}
              />
            ) : undefined,
          placeholder: '',
        },
        {
          component: 'SelectIdentityFormComponent',
          name: 'owner',
          idForLabel: 'useCaseOwner',
          label: 'Use case owner',
          extraClassNames: 'width-maximum mb25',
          value: owner,
          required: true,
          startEditable: true,
          onChange: (selectedIdentity) => {
            setOwner(_.pick(selectedIdentity, ['id', 'name']) as IdentityPreviewV1);
          },
          testId: 'owner',
        },
        {
          component: 'CheckboxFormComponent',
          id: 'grantReadPermissions',
          name: 'grantReadPermissions',
          label: '',
          onChange: async () => {
            setHasReadPermissions(!hasReadPermissions);
          },
          skipStore: true,
          checkboxLabel: t('IMPACT_REPORT.READ_PERMISSIONS_REQUIRED'),
          value: hasReadPermissions,
        },
      ],
    },

    {
      component: 'FormGroup',
      name: 'categorize',
      includeIf: stageIndex >= 1,
      components: [
        {
          component: 'DisplayOnlyFormElementWrapper',
          name: 'categoryHeader',
          children: sectionHeader(1),
        },
        {
          component: 'LabelFormComponent',
          name: 'locationLabel',
          value: 'Location',
          includeIf: showLocation,
        },
        {
          component: 'LocationLookupFormComponent',
          name: 'locationLookup',
          extraClassNames: 'mb8',
          label: 'Location',
          value: location,
          onChange: (location: Location) => {
            setLocation(location);
          },
          testId: 'location',
          required: false,
          includeIf: showLocation,
        },
        {
          component: 'TextFieldFormComponent',
          name: 'site',
          label: 'Site',
          value: site,
          onChange: (site) => {
            setSite(site);
          },
          size: 'sm',
          testId: 'site',
          includeIf: showSite,
        },
        ...categoryFields,
        {
          component: 'TextFieldFormComponent',
          name: 'asset',
          label: 'Asset type',
          value: asset,
          onChange: (asset) => {
            setAsset(asset as string);
          },
          size: 'sm',
          testId: 'assetType',
          includeIf: showAssetPath,
        },
      ],
    },
    {
      component: 'FormGroup',
      name: 'quantify',
      includeIf: stageIndex >= 2,

      components: [
        {
          component: 'DisplayOnlyFormElementWrapper',
          name: 'quantifyHeader',
          children: sectionHeader(2),
        },
        {
          component: 'LabelFormComponent',
          name: 'impactType',
          value: 'Impact type',
          extraClassNames: '',
        },
        {
          component: 'FormRow',
          name: 'justARow',
          components: [
            {
              component: 'CheckboxFormComponent',
              name: 'oneTime',
              type: 'radio',
              value: impactType === 'oneTime',
              onChange: () => {
                setImpactType('oneTime');
                setShowItemSelection(
                  kpiColumns?.reduce((acc, column) => {
                    acc[column.columnName] = false;
                    return acc;
                  }, {} as Record<string, boolean>),
                );
              },
              checkboxLabel: 'One time impact',
              id: 'oneTime',
              testId: 'oneTime',
              skipStore: true,
              classes: 'flexAlignCenter mr20',
            },
            {
              component: 'CheckboxFormComponent',
              name: 'Continuous',
              type: 'radio',
              value: impactType === 'continuous',
              onChange: () => {
                setImpactType('continuous');
              },
              checkboxLabel: 'continuous impact',
              id: 'continuous',
              testId: 'continuous',
              classes: 'flexAlignCenter mr20',
            },
          ],
        },
      ],
    },
    {
      component: 'FormGroup',
      name: 'startEndDateRow',
      includeIf: stageIndex >= 2,
      components: [
        {
          includeIf: impactType !== 'customCalc',
          component: 'FormRow',
          name: 'startEndRow',
          extraClassNames: 'flexAlignCenter',
          components: [
            {
              component: 'DatePickerFormComponent',
              name: 'impactStartDate',
              label: t('IMPACT_REPORT.START_DATE'),
              value: impactStartDate,
              required: true,
              onChange: (date) => {
                setImpactStartDate(date);
              },
              testId: 'impactStartDate',
              wrapperClassNames: 'mr5',
            },
            {
              component: 'DatePickerFormComponent',
              name: 'impactEndDate',
              includeIf: impactType === 'continuous',
              label: t('IMPACT_REPORT.END_DATE'),
              value: impactEndDate,
              onChange: (date) => {
                setImpactEndDate(date);
              },
              testId: 'impactEndDate',
              wrapperClassNames: 'ml15',
            },

            {
              component: 'FormGroup',
              name: 'continuousImpact',
              includeIf: impactType === 'continuous',
              formGroupExtraClassNames: 'ml20 max-width-150',
              components: [
                {
                  label: t('IMPACT_REPORT.FREQUENCY'),
                  component: 'SelectFormComponent',
                  name: 'impactFrequency',
                  value: impactFrequency,
                  options: impactFrequencyOptions,
                  onChange: (frequency) => {
                    setImpactFrequency(frequency as string);
                  },
                },
              ],
            },
          ],
        },
        {
          component: 'FormRow',
          name: 'monetizeFormRow',
          components: [
            {
              component: 'FormGroup',
              name: 'kpis',
              components: [...kpiFields],
            },
          ],
        },
      ],
    },
  ];

  const onFormSubmitCallback = async (values: any) => {
    const properties: Array<ScalarPropertyV1> = [];
    const addIfNotEmpty = (name: string, value: string | boolean | null | undefined) => {
      if (value) {
        properties.push({ name, value });
      }
    };
    addIfNotEmpty(SeeqNames.ImpactReport.ImpactName, title);
    addIfNotEmpty(SeeqNames.ImpactReport.ImpactDescription, description);
    addIfNotEmpty(SeeqNames.ImpactReport.ImpactOwner, owner?.id);

    addIfNotEmpty(SeeqNames.ImpactReport.ImpactSite, site);
    addIfNotEmpty(SeeqNames.ImpactReport.ImpactAsset, asset);

    addIfNotEmpty(SeeqNames.ImpactReport.ImpactSavingsType, impactType);
    addIfNotEmpty(SeeqNames.ImpactReport.ImpactStartDate, impactStartDate && impactStartDate?.toISOString());
    addIfNotEmpty(SeeqNames.ImpactReport.ImpactEndDate, impactEndDate && impactEndDate?.toISOString());

    addIfNotEmpty(SeeqNames.ImpactReport.ImpactFrequency, impactFrequency);

    addIfNotEmpty(SeeqNames.ImpactReport.AssignedToWorksheet, worksheetId);
    addIfNotEmpty(SeeqNames.ImpactReport.AssignedToWorkbook, workbookId);
    addIfNotEmpty(SeeqNames.ImpactReport.ImpactStatus, availableStages[stageIndex]);

    addIfNotEmpty(SeeqNames.ImpactReport.Location, JSON.stringify(location));
    const hasContinuous = _.some(Object.values(showItemSelection), (v) => v === true);
    const unit = _.find(impactFrequencyOptions, { value: impactFrequency })?.label;
    if (hasContinuous && !unit) {
      errorToast({
        messageKey: 'IMPACT_REPORT.INVALID_UNIT',
        messageParams: { unit },
      });
      return;
    }
    // ensure custom kpi values are set
    kpiColumns?.forEach((column) => {
      const columnName = column.columnName;
      let value = customKPIValues[columnName];
      if (!value) return;
      value = typeof value === 'string' || typeof value === 'number' ? value : value.id;
      addIfNotEmpty(columnName, value);
    });

    // if we are updating an existing report, we need to use the existing datum id
    let useThisDatumId = rowDatumId;
    if (useThisDatumId === undefined) {
      doTrack(TRACK_IMPACT, 'Created new Impact Report', { isTopic, impactType });
      useThisDatumId = randomUUID();
    } else {
      doTrack(TRACK_IMPACT, 'Updated existing Impact Report', { isTopic, impactType });
    }

    properties.push({ name: SeeqNames.MaterializedTables.ItemIdColumn, value: tableId });
    properties.push({ name: SeeqNames.MaterializedTables.DatumIdColumn, value: useThisDatumId });

    /**
     * TODO: CRAB-46894:
     * As part of CRAB-46894 please remove the next line of code and ensure that the datum_id column rules are
     * removed so that a simple "datum id" property is used as the value of the datum id.
     *
     *
     * How we got here:
     * Due to the lack of a table type for Materialized Tables a migration that changes the datum_id to be expecting
     * a property named "Existing Row Identifier" is applied to the ImpactReport Table. To ensure updates of impact
     * reports work as expected we need to also include a property called "Existing Row Identifier" with our Events.
     * The value is simple the datum id of the row we are updating (as it's still being returned as the datum id).
     */
    properties.push({ name: SeeqNames.MaterializedTables.UniqueRowId, value: useThisDatumId });

    // remove all previously assigned context labels to avoid problems
    if (categories) {
      const contextLabels: ContextLabelWithIdInputV1[] = _.chain(categories)
        .map((category: ImpactReportCategory) => {
          const labelId = values[category.category.id!];
          return labelId
            ? {
                contextLabelId: report && report[`${category.category.id}_${IMPACT_REPORT_LABEL_ID}`],
                datumId: useThisDatumId,
                labelId,
              }
            : null;
        })
        .compact()
        .value();
      try {
        await sqContextApi.batchCreateContextLabels(contextLabels, { itemId: tableId });
      } catch (error: any) {
        logError(error);
      }
    }

    await addOrUpdateImpactReport(tableId, properties);
    setRowDatumId(useThisDatumId); // ensure this gets set so the "continue" button works

    if (readPermissionRef.current !== hasReadPermissions) {
      if (hasReadPermissions) {
        await giveEveryoneGroupReadPermissions(workbookId!);
      } else {
        await removeEveryoneGroupReadPermissions(workbookId!);
      }
      readPermissionRef.current = hasReadPermissions;
    }
  };

  return (
    <div className="flexRowContainer flexAlignCenter mb10 flexFill valueCapture">
      {!workbookId && !worksheetId && (
        <div className="flexRowContainer mt15 flexFill width-maximum flexAlignCenter">
          <div className="flexColumnContainer flexAlignCenter mbn16 formSectionHeader width-maximum mb10">
            <h4 className="ml10">{t('IMPACT_REPORT.SELECT_WORKSHEET_AND_WORKBOOK')}</h4>
          </div>
          <hr />
          <ReportContentWorksheetAndUrl
            selectOnlyWorkbooks={true}
            allowedTypes={[
              SeeqNames.Types.Topic,
              SeeqNames.Types.Analysis,
              SeeqNames.Types.Vantage,
              SeeqNames.Types.Folder,
            ]}
            extraClassNames="width-maximum"
            setSelectedItem={(item) => {
              setWorkbookId(item?.id);
            }}
          />
          <Button label={t('IMPACT_REPORT.CONTINUE')} disabled={!workbookId} extraClassNames="mt10" />
        </div>
      )}
      {workbookId && !worksheetId && (
        <div className="flexRowContainer mt15 flexFill flexAlignCenter">
          <div className="flexColumnContainer flexAlignCenter mbn16 formSectionHeader width-maximum mb10">
            <h4 className="ml10">{t('IMPACT_REPORT.SELECT_WORKSHEET')}</h4>
          </div>
          <hr />
          <SelectWorksheet
            workbookId={workbookId}
            worksheetIds={[]}
            setWorksheetIds={(worksheetIds) => {
              if (worksheetIds?.length > 0) setWorksheetId(worksheetIds[0]);
            }}
            workstepIds={[]}
            setWorkstepIds={() => {}}
            setTotalWorksheets={() => {}}
            isLoading={false}
          />
          <Button label={t('IMPACT_REPORT.CONTINUE')} disabled={!worksheetId} extraClassNames="mt10" />
        </div>
      )}

      {workbookId && worksheetId && (
        <>
          <SaveAndContinueFormBuilder
            submitBtnLabel="Save & Close"
            submitBtnId="saveAndClose"
            extraClassNames="flexFill tw:overflow-auto height-maximum"
            continueButtonLabel="Save & Continue"
            continueButtonPosition="center"
            continueButtonExtraClassNames="mr10"
            continueButtonEnabled={stageIndex < availableStages.length - 1}
            continueButtonOnSubmit={async (values) => {
              await onFormSubmitCallback(values);
              if (stageIndex < availableStages.length - 1) {
                setStageIndex(stageIndex + 1);
              }
              doTrack(TRACK_IMPACT, 'Save and Continue Button', { stageIndex });
            }}
            continueButtonVariant="theme"
            formDefinition={formDefinition}
            submitFn={async (values) => {
              await onFormSubmitCallback(values);
              setRowDatumId(undefined);
              closeFn(true);
              setUpdateNominations(true);
              doTrack(TRACK_IMPACT, 'Save and Close Button', { stageIndex });
            }}
            closeFn={closeFn}
          />
        </>
      )}
    </div>
  );
};
